import { useState, useEffect, useCallback, useRef } from "react";
import Table from "../Table/Table";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Bounce, toast } from "react-toastify";
import "./followUp.css";
import PopUp from "../PopUp";
import { buildFilterQueryParams } from "../utils/queryHelpers";
import useDebouncedValue from "../hooks/useDebouncedValue";

const FollowUp = () => {
  const [loading, setLoading] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [showPopUp, setShowPopUp] = useState(false);
  const [selectedFollowUpId, setSelectedFollowUpId] = useState(null);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnFilterFns, setColumnFilterFns] = useState({});
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const debouncedSorting = useDebouncedValue(sorting, 400);
  const debouncedColumnFilters = useDebouncedValue(columnFilters, 400);
  const downloadRef = useRef(null);

  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const isoDatePart = dateString.slice(0, 10);
    const [year, month, day] = isoDatePart.split("-");
    if (!year || !month || !day) return "";

    return `${day}.${month}.${year}`;
  };

  const fetchFollowups = useCallback(
    async ({
      pageParam = page,
      sizeParam = pageSize,
      sortingParam = debouncedSorting,
      filtersParam = debouncedColumnFilters,
      filterFnsParam = columnFilterFns,
    } = {}) => {
      setLoading(true);
      try {
        const sortStrings = (sortingParam ?? []).map(
          ({ id, desc }) => `${id},${desc ? "desc" : "asc"}`
        );

        // Transform trainer and autore field filters to use nested paths (trainer.nome, autore.nome, etc.)
        const transformedFilters = (filtersParam ?? []).map((filter) => {
          // Trainer fields
          if (filter.id === "trainerNome") {
            return { ...filter, id: "trainer.nome" };
          }
          if (filter.id === "trainerCognome") {
            return { ...filter, id: "trainer.cognome" };
          }
          if (filter.id === "trainerEmail") {
            return { ...filter, id: "trainer.email" };
          }
          // Autore fields
          if (filter.id === "autoreNome") {
            return { ...filter, id: "autore.nome" };
          }
          if (filter.id === "autoreEmail") {
            return { ...filter, id: "autore.email" };
          }
          return filter;
        });

        // Transform filter functions to match the new field names
        const transformedFilterFns = { ...filterFnsParam };
        // Trainer filter functions
        if (filterFnsParam["trainerNome"]) {
          transformedFilterFns["trainer.nome"] = filterFnsParam["trainerNome"];
          delete transformedFilterFns["trainerNome"];
        }
        if (filterFnsParam["trainerCognome"]) {
          transformedFilterFns["trainer.cognome"] = filterFnsParam["trainerCognome"];
          delete transformedFilterFns["trainerCognome"];
        }
        if (filterFnsParam["trainerEmail"]) {
          transformedFilterFns["trainer.email"] = filterFnsParam["trainerEmail"];
          delete transformedFilterFns["trainerEmail"];
        }
        // Autore filter functions
        if (filterFnsParam["autoreNome"]) {
          transformedFilterFns["autore.nome"] = filterFnsParam["autoreNome"];
          delete transformedFilterFns["autoreNome"];
        }
        if (filterFnsParam["autoreEmail"]) {
          transformedFilterFns["autore.email"] = filterFnsParam["autoreEmail"];
          delete transformedFilterFns["autoreEmail"];
        }

        const filterParams = buildFilterQueryParams(
          transformedFilters,
          transformedFilterFns
        );

        const params = {
          page: pageParam,
          size: sizeParam,
          ...filterParams,
        };

        if (sortStrings.length > 0) {
          params.sort = sortStrings.join(";");
        }

        const response = await api.get(`/followup`, {
          params,
        });

        const { content = [], page: pageInfo } = response.data ?? {};

        const flattened = content.map((item) => ({
          id: item.id,
          effettuato: item.effettuato ? "Sì" : "No",
          trainerNome: item.trainer?.nome || "",
          trainerCognome: item.trainer?.cognome || "",
          trainerEmail: item.trainer?.email || "",
          dataFormazione: formatDate(item.dataFormazione),
          autoreNome: (item.autore?.nome + " " + item.autore?.cognome) || "",
          autoreEmail: item.autore?.email || "",
          location: item.location || "",
          note: item.note || "",
        }));

        setFollowups(flattened);
        setTotalRows(pageInfo?.totalElements ?? content.length);
      } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize, debouncedSorting, debouncedColumnFilters, columnFilterFns]
  );

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");
    if (userRole !== "ADMIN") {
      alert("Accedi come amministratore per visualizzare i follow-up!");
      navigate("/login");
      localStorage.clear();
    } else {
      fetchFollowups();
    }
  }, [navigate, fetchFollowups]);

  const handlePageChange = (pageNum) => {
    setPage(Math.max(pageNum - 1, 0));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
  };

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
    setPage(0);
  };

  const handleColumnFiltersChange = (newFilters) => {
    setColumnFilters(newFilters);
    setPage(0);
  };

  const handleColumnFilterFnsChange = (newFilterFns) => {
    setColumnFilterFns(newFilterFns);
    setPage(0);
  };

  const handleRowClick = (followup) => {
    navigate(`/follow-up/${followup.id}`);
  };

  const handleDelete = async (id) => {
    if (!selectedFollowUpId) return;

    try {
      const response = await api.delete(`/followup/${selectedFollowUpId}`);
      if (response.status === 204) {
        toast.success("Follow-up eliminato con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        fetchFollowups(page - 1);
      }
    } catch (error) {
      console.error(error);

      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setShowPopUp(false);
      setSelectedFollowUpId(null);
    }
  };

  const columns = [
    { field: "effettuato", header: "Effettuato" },
    { field: "trainerNome", header: "Nome docente" },
    { field: "trainerCognome", header: "Cognome docente" },
    { field: "trainerEmail", header: "Email docente" },
    { field: "dataFormazione", header: "Data Formazione" },
    { field: "autoreNome", header: "Autore" },
    { field: "autoreEmail", header: "Email Autore" },
    { field: "location", header: "Location" },
    { field: "note", header: "Note" },
    {
      header: "Azioni",
      accessorKey: "azioni",
      Cell: ({ row }) => (
        <button
          className="outside-delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedFollowUpId(row.original.id);
            setShowPopUp(true);
          }}
        >
          <i className="fa-solid fa-trash"></i> Elimina
        </button>
      ),
    },
  ];

  const handleExport = async (format) => {
    setIsDownloadOpen(false);
    setLoading(true);
    try {
      const response = await api.get("/export/followup", {
        params: { format },
        responseType: "blob",
      });
      
      if (response.status === 204) {
        toast.info("Non ci sono dati da scaricare", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        return;
      }
      
      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const contentDisposition = response.headers["content-disposition"];
      let filename = `followup.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Errore durante il download", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setIsDownloadOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <PopUp
        open={showPopUp}
        setOpen={setShowPopUp}
        header="Conferma Eliminazione"
        content="Sei sicuro di voler rimuovere questo follow-up?"
        deleteFunction={handleDelete}
      />

      <div className="page-header">
        <div className="page-title">Follow Up</div>
        <div className="controls">
          <div className="download-menu" ref={downloadRef}>
            <button
              className="download-btn"
              onClick={() => setIsDownloadOpen((prev) => !prev)}
            >
              Scarica
            </button>
            {isDownloadOpen && (
              <div className="download-dropdown">
                <button onClick={() => handleExport("csv")}>CSV</button>
                <button onClick={() => handleExport("xlsx")}>Excel</button>
              </div>
            )}
          </div>
          <div className="add-btn">
            <button onClick={() => navigate("/aggiungi-follow-up")}>
              + Nuovo Follow Up
            </button>
          </div>
        </div>
      </div>

      <div className="page-content followup">
        <Table
          columns={columns}
          data={followups}
          onRowClick={handleRowClick}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRows={totalRows}
          perPage={pageSize}
          currentPage={page + 1}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnFilters={columnFilters}
          onColumnFiltersChange={handleColumnFiltersChange}
          columnFilterFns={columnFilterFns}
          onColumnFilterFnsChange={handleColumnFilterFnsChange}
        />
      </div>
    </div>
  );
};

export default FollowUp;
